import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { fetchAuthConfig, buildMsalConfig } from './authConfig';
import { tokenRefreshService } from './utils/tokenRefreshService';
import './index.css';

// Loading component to show while fetching config
const LoadingScreen = () => (
    <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#1a1a1a',
        color: '#fff',
        fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
        <div style={{ textAlign: 'center' }}>
            <div style={{
                border: '4px solid #333',
                borderTop: '4px solid #fff',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
            }}></div>
            <p>Loading authentication configuration...</p>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    </div>
);

// Initialize MSAL asynchronously with runtime configuration
const initializeMsal = async () => {
    try {
        // Fetch configuration from backend
        const authConfig = await fetchAuthConfig();
        const msalConfig = buildMsalConfig(authConfig);

        console.log('Initializing MSAL with runtime configuration');
        const msalInstance = new PublicClientApplication(msalConfig);

        // Wait for MSAL to initialize
        await msalInstance.initialize();

        // Default to using the first account if no account is active on page load
        if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
            msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
        }

        // Listen for sign-in event and set active account
        msalInstance.addEventCallback((event) => {
            if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
                const account = event.payload.account;
                msalInstance.setActiveAccount(account);

                // Start token refresh service after successful login
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0 && accounts[0].idToken) {
                    tokenRefreshService.startTokenRefresh(
                        msalInstance,
                        accounts,
                        accounts[0].idToken
                    );
                }
            }

            if (event.eventType === EventType.LOGOUT_SUCCESS) {
                // Stop token refresh on logout
                tokenRefreshService.stopTokenRefresh();
            }
        });

        // If user is already logged in on page load, start token refresh
        const existingAccounts = msalInstance.getAllAccounts();
        if (existingAccounts.length > 0 && existingAccounts[0].idToken) {
            tokenRefreshService.startTokenRefresh(
                msalInstance,
                existingAccounts,
                existingAccounts[0].idToken
            );
        }

        return msalInstance;
    } catch (error) {
        console.error('Failed to initialize MSAL:', error);
        throw error;
    }
};

// Render app with async MSAL initialization
const root = createRoot(document.getElementById('root'));

// Show loading screen initially
root.render(<LoadingScreen />);

// Initialize MSAL and render app
initializeMsal()
    .then(msalInstance => {
        root.render(
            <MsalProvider instance={msalInstance}>
                <App />
            </MsalProvider>
        );
    })
    .catch(error => {
        console.error('Fatal error initializing application:', error);
        root.render(
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#1a1a1a',
                color: '#fff',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                padding: '20px'
            }}>
                <div style={{ textAlign: 'center', maxWidth: '600px' }}>
                    <h1 style={{ color: '#ff4444' }}>⚠️ Initialization Error</h1>
                    <p>Failed to load authentication configuration.</p>
                    <p style={{ color: '#999', fontSize: '14px' }}>{error.message}</p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            marginTop: '20px',
                            padding: '10px 20px',
                            backgroundColor: '#444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    });